import { useEffect, useMemo } from 'react';

import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import AnalyticsFieldset from '@/modules/analytics/components/fieldsets/AnalyticsFieldset/AnalyticsFieldset';
import useLineCard from '@/modules/analytics/hooks/useLineCard';
import { getGrowthArrowColor } from '@/util/analytics';
import { subMonthsFromNow, formatDate } from '@/util/dateUtils';

import LineAnalytics from './LineAnalytics';

import styles from './HomeAnalytics.module.scss';

interface Props {
  setAnalyticsEmpty?: (empty: boolean) => void;
}

const HomeAnalytics = ({ setAnalyticsEmpty }: Props) => {
  const deskId = useDeskId();
  const deskIds = `{${deskId}}`;
  const { t } = useTranslation();

  const queryProps = useMemo(
    () => ({
      startDate: formatDate(subMonthsFromNow(1)),
      endDate: formatDate(new Date()),
      deskIds,
    }),
    [deskIds]
  );
  const sessions = useLineCard({
    intervalProps: { type: 'sessions', ...queryProps },
    skip: !deskId,
  });
  const messages = useLineCard({
    intervalProps: { type: 'dialogs', ...queryProps },
    skip: !deskId,
  });

  const fieldsets = [messages, sessions];

  const isLoading = sessions?.isLoading || messages?.isLoading;
  const isAnalyticsEmpty = sessions?.data?.counts === 0;
  useEffect(() => {
    setAnalyticsEmpty(isAnalyticsEmpty);
  }, [isAnalyticsEmpty, setAnalyticsEmpty]);

  if (!sessions?.data?.lineData || !messages?.data?.lineData) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      {fieldsets?.map(({ data }) => {
        const { growth, title, counts } = data ?? {};
        const arrowColor = getGrowthArrowColor(growth);
        const colors = [arrowColor, 'var(--color-foreground-muted)'];
        return (
          <div className={styles.fieldsetWrapper} key={`${deskId}-${title}`}>
            <AnalyticsFieldset
              hasFooter={false}
              fullWidth
              title={title}
              hasExport={false}
              isLoading={isLoading}
              isEmpty={isAnalyticsEmpty}
              size="small"
            >
              {!isAnalyticsEmpty && (
                <div className={styles.content}>
                  <div className={styles.header}>
                    <span className="text-3xl leading-9 font-semibold">{counts}</span>
                    {typeof growth === 'number' && (
                      <p
                        className={cn(styles.percentage, {
                          [styles.success]: growth > 0,
                          [styles.error]: growth < 0,
                        })}
                      >
                        {growth < 0 ? (
                          <ArrowDownIcon size={12} color={arrowColor} />
                        ) : (
                          <ArrowUpIcon size={12} color={arrowColor} />
                        )}
                        <span className="text-sm leading-5 font-semibold">
                          <span style={{ color: arrowColor }}>{growth} %</span>
                          <span className={styles.desc}>{t('home.vs_last_month')}</span>
                        </span>
                      </p>
                    )}
                  </div>
                  <div className={styles.chartContainer}>
                    <LineAnalytics data={data?.lineData} colors={colors} />
                  </div>
                </div>
              )}
            </AnalyticsFieldset>
          </div>
        );
      })}
    </div>
  );
};

export default HomeAnalytics;
